import { useEffect, useId, useMemo, useState } from "react";
import { CoreComposer } from "./CoreComposer";
import { CoreIcon } from "./CoreIcon";
import type {
  CoreComposerProps,
  KnowledgePlaybook,
  KnowledgeSourcesSelection,
  KnowledgeSourceTab,
  MaybePromise,
} from "./types";

const sourceTabs: Array<{ id: KnowledgeSourceTab; label: string; meta: string }> = [
  { id: "playbooks", label: "Playbooks", meta: "Published firm standards" },
  { id: "regional", label: "Regional knowledge", meta: "12 jurisdictions" },
  { id: "web", label: "Web Search", meta: "Current public sources" },
  { id: "case", label: "US Case Law", meta: "Federal courts" },
  { id: "edgar", label: "EDGAR", meta: "SEC company filings" },
];

const jurisdictions = [
  "European Union", "United Kingdom", "United States", "Canada",
  "Australia", "Singapore", "Hong Kong", "Germany",
  "France", "Netherlands", "Ireland", "Switzerland",
];

const materialCategories = [
  "Legislation", "Regulations", "Judgments", "Regulator guidance",
  "Consultations", "Executive orders",
];

export interface KnowledgeSourcesDialogProps {
  open: boolean;
  playbooks?: KnowledgePlaybook[];
  availability?: Partial<Record<KnowledgeSourceTab, {
    status: "available" | "unconfigured" | "unavailable" | string;
    message?: string;
  }>>;
  initialSelection?: Partial<KnowledgeSourcesSelection>;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (selection: KnowledgeSourcesSelection) => MaybePromise;
}

function withNewestLimit(items: string[], next: string, limit: number) {
  if (items.includes(next)) return items.filter((item) => item !== next);
  return [...items, next].slice(-limit);
}

export function KnowledgeSourcesDialog({
  open,
  playbooks = [],
  availability = {},
  initialSelection,
  busy = false,
  onClose,
  onConfirm,
}: KnowledgeSourcesDialogProps) {
  const titleId = useId();
  const [tab, setTab] = useState<KnowledgeSourceTab>("playbooks");
  const [categoryIds, setCategoryIds] = useState<KnowledgeSourceTab[]>(initialSelection?.categoryIds || []);
  const [playbookId, setPlaybookId] = useState(initialSelection?.playbookId || "");
  const [applyPlaybookTo, setApplyPlaybookTo] = useState<"counterparty" | "template" | "both">(
    initialSelection?.applyPlaybookTo || "counterparty"
  );
  const [selectedJurisdictions, setSelectedJurisdictions] = useState(initialSelection?.jurisdictions || []);
  const [selectedMaterials, setSelectedMaterials] = useState(initialSelection?.materialCategories || []);
  const [includeDomains, setIncludeDomains] = useState(initialSelection?.includeDomains || "");
  const [excludeDomains, setExcludeDomains] = useState(initialSelection?.excludeDomains || "");
  const [courtScope, setCourtScope] = useState<"all-federal" | "supreme" | "appeals" | "district">(
    initialSelection?.courtScope || "all-federal"
  );
  const [edgarFiler, setEdgarFiler] = useState(initialSelection?.edgarFiler || "");

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose, open]);

  if (!open) return null;

  const selected = categoryIds.includes(tab);
  const submit = () => onConfirm({
    categoryIds,
    playbookId: playbookId || undefined,
    applyPlaybookTo,
    jurisdictions: selectedJurisdictions,
    materialCategories: selectedMaterials,
    includeDomains: includeDomains || undefined,
    excludeDomains: excludeDomains || undefined,
    courtScope,
    edgarFiler: edgarFiler || undefined,
  });

  return (
    <div className="di-core-dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="di-core-dialog di-core-dialog--sources" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="di-core-dialog__header">
          <div>
            <h2 id={titleId}>Choose sources</h2>
            <p>Attach up to two specialist sources. State in the prompt how each should be used.</p>
          </div>
          <button type="button" className="di-core-btn di-core-btn--secondary di-core-btn--icon" aria-label="Close sources" onClick={onClose}>
            <CoreIcon name="close" />
          </button>
        </header>
        <div className="di-core-source-dialog__body">
          <nav className="di-core-source-dialog__tabs" aria-label="Knowledge source categories">
            {sourceTabs.map((item) => (
              (() => {
                const sourceAvailability = availability[item.id];
                const unavailable = sourceAvailability && sourceAvailability.status !== "available";
                return (
              <button
                key={item.id}
                type="button"
                className={tab === item.id ? "is-active" : ""}
                aria-current={tab === item.id ? "page" : undefined}
                onClick={() => setTab(item.id)}
              >
                <span>{item.label}</span>
                <small>{unavailable ? sourceAvailability.message || "Not configured" : item.meta}</small>
                {categoryIds.includes(item.id) && <CoreIcon name="check" size={13} />}
              </button>
                );
              })()
            ))}
          </nav>
          <div className="di-core-source-dialog__panel">
            <label className="di-core-source-dialog__use">
              <input
                type="checkbox"
                checked={selected}
                disabled={availability[tab] !== undefined && availability[tab]?.status !== "available"}
                onChange={() => setCategoryIds((current) => withNewestLimit(current, tab, 2) as KnowledgeSourceTab[])}
              />
              <span>Use {sourceTabs.find((item) => item.id === tab)?.label}</span>
            </label>
            {availability[tab] !== undefined && availability[tab]?.status !== "available" ? (
              <p className="di-core-callout" role="status">
                {availability[tab]?.message || "This source is not configured for this workspace."}
              </p>
            ) : null}

            {tab === "playbooks" && (
              <>
                <h3>Published playbooks</h3>
                <div className="di-core-choice-list">
                  {playbooks.map((playbook) => (
                    <label key={playbook.id} className="di-core-radio">
                      <input
                        type="radio"
                        name={`${titleId}-playbook`}
                        checked={playbookId === playbook.id}
                        onChange={() => {
                          setPlaybookId(playbook.id);
                          if (!categoryIds.includes("playbooks")) setCategoryIds((current) => withNewestLimit(current, "playbooks", 2) as KnowledgeSourceTab[]);
                        }}
                      />
                      <span className="di-core-radio__dot" />
                      <span>{playbook.name}<small>{playbook.ruleCount} positions</small></span>
                    </label>
                  ))}
                  {!playbooks.length && <p className="di-core-empty-copy">No published playbooks are available.</p>}
                </div>
                <fieldset className="di-core-fieldset">
                  <legend>Apply to</legend>
                  {([
                    ["counterparty", "The uploaded counterparty draft"],
                    ["template", "Our client template only"],
                    ["both", "Both documents · report the gap"],
                  ] as const).map(([id, label]) => (
                    <label key={id} className="di-core-radio">
                      <input type="radio" checked={applyPlaybookTo === id} onChange={() => setApplyPlaybookTo(id)} />
                      <span className="di-core-radio__dot" />
                      {label}
                    </label>
                  ))}
                </fieldset>
              </>
            )}

            {tab === "regional" && (
              <>
                <div className="di-core-source-dialog__heading">
                  <div><h3>Jurisdictions</h3><p>Select up to two. Selecting a third replaces the oldest.</p></div>
                  <span>{selectedJurisdictions.length} of 2</span>
                </div>
                <div className="di-core-check-grid">
                  {jurisdictions.map((jurisdiction) => (
                    <label key={jurisdiction}>
                      <input
                        type="checkbox"
                        checked={selectedJurisdictions.includes(jurisdiction)}
                        onChange={() => {
                          setSelectedJurisdictions((current) => withNewestLimit(current, jurisdiction, 2));
                          if (!categoryIds.includes("regional")) setCategoryIds((current) => withNewestLimit(current, "regional", 2) as KnowledgeSourceTab[]);
                        }}
                      />
                      <span>{jurisdiction}</span>
                    </label>
                  ))}
                </div>
                <h3>Material categories</h3>
                <div className="di-core-chip-checks">
                  {materialCategories.map((item) => (
                    <button
                      key={item}
                      type="button"
                      aria-pressed={selectedMaterials.includes(item)}
                      onClick={() => setSelectedMaterials((current) => current.includes(item) ? current.filter((candidate) => candidate !== item) : [...current, item])}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </>
            )}

            {tab === "web" && (
              <>
                <h3>Web Search controls</h3>
                <label className="di-core-field">Include domains<input value={includeDomains} onChange={(event) => setIncludeDomains(event.currentTarget.value)} placeholder="example.com, regulator.gov" /></label>
                <label className="di-core-field">Exclude domains<input value={excludeDomains} onChange={(event) => setExcludeDomains(event.currentTarget.value)} placeholder="blog.example.com" /></label>
                <p className="di-core-callout">Domain controls depend on the search subprocessor and are not absolute restrictions. Review cited sources before relying on an answer.</p>
              </>
            )}

            {tab === "case" && (
              <fieldset className="di-core-fieldset">
                <legend>Court scope</legend>
                {([
                  ["all-federal", "All federal courts"],
                  ["supreme", "US Supreme Court"],
                  ["appeals", "Federal courts of appeals"],
                  ["district", "Federal district courts"],
                ] as const).map(([id, label]) => (
                  <label key={id} className="di-core-radio">
                    <input type="radio" checked={courtScope === id} onChange={() => setCourtScope(id)} />
                    <span className="di-core-radio__dot" />
                    {label}
                  </label>
                ))}
              </fieldset>
            )}

            {tab === "edgar" && (
              <>
                <h3>Company filings</h3>
                <label className="di-core-field">Filer or ticker<input value={edgarFiler} onChange={(event) => setEdgarFiler(event.currentTarget.value)} placeholder="Microsoft or MSFT" /></label>
                <p className="di-core-muted">DI searches public SEC filings and cites the filing and section used.</p>
              </>
            )}
          </div>
        </div>
        <footer className="di-core-dialog__footer">
          <span>{categoryIds.length} of 2 sources selected · Assistant supports up to two.</span>
          <span className="di-core-spacer" />
          <button type="button" className="di-core-btn di-core-btn--secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="di-core-btn di-core-btn--primary" disabled={busy || categoryIds.length === 0} onClick={() => void submit()}>
            {busy ? "Saving…" : "Confirm selection"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export interface AssistantScreenProps extends Omit<CoreComposerProps, "onOpenSources"> {
  sourceDialog?: Omit<KnowledgeSourcesDialogProps, "open" | "onClose">;
  onOpenSources?: () => void;
  heading?: string;
  intro?: string;
}

export function AssistantScreen({
  heading = "Ask, analyse, draft.",
  intro = "Ground the answer in uploaded files, the project's files, the Contract Repository, a playbook, Web Search or regional legal sources. Every response carries citations back to the material it used.",
  sourceDialog,
  onOpenSources,
  ...composerProps
}: AssistantScreenProps) {
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const canOpenSources = Boolean(sourceDialog || onOpenSources);
  const sourceSelection = useMemo(() => sourceDialog?.initialSelection, [sourceDialog?.initialSelection]);

  return (
    <main className="di-core-screen di-core-assistant">
      <div className="di-core-assistant__content">
        <p className="di-core-kicker">New thread</p>
        <h1>{heading}</h1>
        <p className="di-core-assistant__intro">{intro}</p>
        <CoreComposer
          {...composerProps}
          submitLabel={composerProps.submitLabel || "Ask DI"}
          onOpenSources={canOpenSources ? () => {
            if (sourceDialog) setSourcesOpen(true);
            else onOpenSources?.();
          } : undefined}
        />
      </div>
      {sourceDialog && (
        <KnowledgeSourcesDialog
          {...sourceDialog}
          initialSelection={sourceSelection}
          open={sourcesOpen}
          onClose={() => setSourcesOpen(false)}
          onConfirm={async (selection) => {
            await sourceDialog.onConfirm(selection);
            setSourcesOpen(false);
          }}
        />
      )}
    </main>
  );
}
